import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { FileCheck2, ChevronDown, ChevronUp, Download, CheckCircle2, AlertTriangle } from 'lucide-react';
import activityService from '../../services/activityService';
import kySoService from '../../services/kySoService';
import xacNhanRenLuyenService from '../../services/xacNhanRenLuyenService';
import Card from '../common/Card';
import Button from '../common/Button';

/**
 * "Tạo xác nhận hoạt động đã tham gia" — Đoàn trường bấm 1 lần, tạo riêng 1 file PDF
 * (đã ký số + con dấu + khoá) cho mỗi sinh viên đã tham gia hoạt động có điểm rèn luyện
 * trong học kỳ đã chọn. File tự động gắn chữ ký "Người lập" đã chọn sẵn.
 */
export default function XacNhanHoatDongPanel() {
  const [open, setOpen] = useState(false);
  const [soHocKy, setSoHocKy] = useState('');
  const [maNamHoc, setMaNamHoc] = useState('');
  const [chuKyId, setChuKyId] = useState('');
  const [conDauId, setConDauId] = useState('');
  const [khoaFilePdf, setKhoaFilePdf] = useState(true);
  const [apDungGiapLai, setApDungGiapLai] = useState(true);
  const [result, setResult] = useState(null);

  const { data: academicInfo } = useQuery({
    queryKey: ['academic-info-xn'],
    queryFn: activityService.getCurrentAcademicInfo,
    enabled: open,
  });
  useEffect(() => {
    if (!academicInfo) return;
    if (!soHocKy && academicInfo.soHocKy) setSoHocKy(String(academicInfo.soHocKy));
    if (!maNamHoc && academicInfo.maNamHoc) setMaNamHoc(academicInfo.maNamHoc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicInfo]);
  const { data: chuKyList } = useQuery({
    queryKey: ['chu-ky-full-xn'], queryFn: () => kySoService.getAllChuKy('FULL'), enabled: open,
  });
  const { data: conDauList } = useQuery({
    queryKey: ['con-dau-xn'], queryFn: kySoService.getAllConDau, enabled: open,
  });

  const chuKySelected = chuKyList?.find((c) => String(c.id) === String(chuKyId));

  const mutTao = useMutation({
    mutationFn: () => xacNhanRenLuyenService.tao({
      soHocKy: Number(soHocKy), maNamHoc,
      chuKyNguoiLapId: chuKyId ? Number(chuKyId) : null,
      tenNguoiLap: chuKySelected?.tenNguoiKy || '',
      chucVuNguoiLap: chuKySelected?.chucVu || '',
      conDauId: conDauId ? Number(conDauId) : null,
      apDungGiapLai, khoaFilePdf,
    }),
    onSuccess: (data) => { setResult(data); toast.success(`Đã tạo ${data.thanhCong} file xác nhận`); },
    onError: (e) => toast.error(e?.response?.data?.message || 'Tạo xác nhận thất bại'),
  });

  const submit = () => {
    if (!soHocKy || !maNamHoc) return toast.error('Chọn học kỳ và năm học');
    if (!chuKyId) return toast.error('Chọn chữ ký "Người lập"');
    if (!window.confirm(
      `Tạo xác nhận hoạt động cho TOÀN BỘ sinh viên đã tham gia trong Học kỳ ${soHocKy} - ${maNamHoc}?\n` +
      `Mỗi sinh viên có ≥1 hoạt động tính điểm rèn luyện sẽ nhận 1 file riêng, có thể tải trong mục Điểm rèn luyện của họ.`
    )) return;
    setResult(null);
    mutTao.mutate();
  };

  return (
    <Card className="!p-0 overflow-hidden">
      <button onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-4 hover:bg-slate-50/60 text-left">
        <span className="inline-flex items-center gap-2 font-semibold text-slate-800">
          <FileCheck2 className="w-5 h-5 text-indigo-600" />
          Tạo xác nhận hoạt động đã tham gia
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="p-4 sm:p-5 border-t border-slate-100 space-y-4">
          <p className="text-sm text-slate-500">
            Tạo 1 file PDF xác nhận (có chữ ký, con dấu, đã khoá) cho mỗi sinh viên đã tham gia hoạt
            động tính điểm rèn luyện trong học kỳ chọn bên dưới — dùng làm minh chứng nộp HTQL.
            Sinh viên chỉ thấy được file của chính mình trong mục Điểm rèn luyện.
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="form-label">Học kỳ</label>
              <select className="input-base" value={soHocKy} onChange={(e) => setSoHocKy(e.target.value)}>
                <option value="">— Chọn —</option>
                <option value="1">Học kỳ 1</option>
                <option value="2">Học kỳ 2</option>
                <option value="3">Học kỳ 3</option>
              </select>
            </div>
            <div>
              <label className="form-label">Năm học</label>
              <select className="input-base" value={maNamHoc} onChange={(e) => setMaNamHoc(e.target.value)}>
                <option value="">— Chọn —</option>
                {(academicInfo?.danhSachNamHoc || []).map((n) => (
                  <option key={n.maNamHoc} value={n.maNamHoc}>{n.tenNamHoc || n.maNamHoc}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Chữ ký "Người lập"</label>
              <select className="input-base" value={chuKyId} onChange={(e) => setChuKyId(e.target.value)}>
                <option value="">— Chọn —</option>
                {(chuKyList || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.tenNguoiKy}{c.chucVu ? ` (${c.chucVu})` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Con dấu (tuỳ chọn)</label>
              <select className="input-base" value={conDauId} onChange={(e) => setConDauId(e.target.value)}>
                <option value="">— Không đóng dấu —</option>
                {(conDauList || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.ten}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={apDungGiapLai} onChange={(e) => setApDungGiapLai(e.target.checked)} />
              Đóng dấu giáp lai (nếu có nhiều trang)
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={khoaFilePdf} onChange={(e) => setKhoaFilePdf(e.target.checked)} />
              Khoá file (không cho sửa/copy nội dung)
            </label>
          </div>

          <Button isLoading={mutTao.isPending} onClick={submit}>
            Tạo xác nhận cho toàn trường
          </Button>

          {result && (
            <div className="rounded-lg bg-slate-50 p-4 text-sm space-y-1">
              <p className="flex items-center gap-2 text-emerald-700 font-medium">
                <CheckCircle2 className="w-4 h-4" /> {result.thanhCong}/{result.tongSoSinhVien} sinh viên đã tạo file thành công
              </p>
              {result.loi > 0 && (
                <div className="text-rose-600">
                  <p className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {result.loi} lỗi:</p>
                  <ul className="list-disc ml-6">
                    {result.loiChiTiet?.map((l, i) => <li key={i}>{l}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
